/**
 * Menu Planning Occasions Page
 * 
 * Displays all menu planning occasions and allows users to generate schedules from them
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { useNavigate } from 'react-router-dom';
import { useMenuScheduling } from '../context/menuSchedulingContext';
import {
    getOccasions,
    getOccasion
} from '../services/api';
import menuPlanningService from '../services/menuPlanningService';

const MenuPlanningOccasionsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { openScheduleModal } = useMenuScheduling();
    
    const [occasions, setOccasions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generatingSchedule, setGeneratingSchedule] = useState(false);
    const [selectedOccasionId, setSelectedOccasionId] = useState(null);

    useEffect(() => {
        if (user) {
            loadOccasions();
        }
    }, [user]);

    const loadOccasions = async () => {
        if (!user) return;
        
        try {
            setLoading(true);
            const token = await user.getIdToken();
            // Load only menu_plan occasions
            const response = await getOccasions(token, 'menu_plan');
            setOccasions(response.occasions || []);
        } catch (err) {
            console.error('Error loading occasions:', err);
            setError(err.message || 'Failed to load occasions');
        } finally {
            setLoading(false);
        }
    };

    const handleOccasionClick = async (occasionId) => {
        if (!user) return;
        
        try {
            setGeneratingSchedule(true);
            setSelectedOccasionId(occasionId);
            setError(null);
            const token = await user.getIdToken();
            
            // First, check if there's an existing menu plan for this occasion
            const existingPlan = await menuPlanningService.getMenuPlanByOccasion(occasionId, token);
            
            if (existingPlan && existingPlan.schedule_data) {
                // Load existing schedule
                const plan = {
                    recipes: existingPlan.schedule_data.recipes || [],
                    segments: existingPlan.schedule_data.segments || [],
                    makespanSec: existingPlan.makespanSec || 0,
                    activeTotalSec: existingPlan.schedule_data.activeTotalSec || 0,
                    inactiveTotalSec: existingPlan.schedule_data.inactiveTotalSec || 0
                };
                
                // Navigate to schedule page with existing plan
                navigate('/schedule', { 
                    state: { 
                        schedule: plan, 
                        occasionId: occasionId,
                        menuPlanId: existingPlan.id
                    } 
                });
                return;
            }
            
            // No existing plan, generate new schedule
            // Get occasion with recipes
            const occasionResponse = await getOccasion(occasionId, token);
            const occasion = occasionResponse.occasion;
            
            if (!occasion || !occasion.recipes || occasion.recipes.length === 0) {
                setError('This occasion has no recipes. Please add recipes to the occasion first.');
                setGeneratingSchedule(false);
                return;
            }
            
            // Extract recipe IDs and order
            const recipeIds = occasion.recipes.map(r => r.recipe_id);
            
            // Generate schedule
            const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            const response = await fetch(`${apiBaseUrl}/menu-planning/schedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    recipe_ids: recipeIds,
                    options: {
                        priority: 'recipeOrder',
                        recipe_order: recipeIds
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate schedule');
            }

            const scheduleData = await response.json();

            // Get recipe details for the schedule
            const recipes = occasion.recipes.map(r => ({
                id: r.recipe_id,
                name: r.recipe_name || `Recipe ${r.recipe_id}`
            }));

            const plan = {
                recipes: recipes,
                segments: scheduleData.segments,
                makespanSec: scheduleData.makespanSec,
                activeTotalSec: scheduleData.activeTotalSec,
                inactiveTotalSec: scheduleData.inactiveTotalSec
            };
            
            // Save the menu plan with occasion_id
            try {
                const savedPlan = await menuPlanningService.saveMenuPlan(
                    occasion.name,
                    recipeIds,
                    plan,
                    token,
                    occasionId
                );
                
                // Navigate to schedule page with saved plan
                navigate('/schedule', { 
                    state: { 
                        schedule: plan, 
                        occasionId: occasionId,
                        menuPlanId: savedPlan.id
                    } 
                });
            } catch (saveErr) {
                console.warn('Failed to save menu plan:', saveErr);
                // Still navigate even if save fails
                navigate('/schedule', { state: { schedule: plan, occasionId: occasionId } });
            }
        } catch (err) {
            console.error('Error loading/generating schedule from occasion:', err);
            setError(err.message || 'Failed to load schedule');
        } finally {
            setGeneratingSchedule(false);
            setSelectedOccasionId(null);
        }
    };

    return (
        <div className="min-h-screen bg-[#EEEEE7] px-0 py-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-neutral-0 p-6 hairline mb-6">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="font-sans text-5xl font-medium text-[#1b263b]">
                            Menu Planning Occasions
                        </h1>
                        <button
                            onClick={openScheduleModal}
                            className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                            style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.6'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                        >
                            New Schedule
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-600 text-red-900">
                            <p className="font-sans text-sm">{error}</p>
                        </div>
                    )}

                    {loading ? (
                        <p className="font-sans text-base text-neutral-900 opacity-60">Loading occasions...</p>
                    ) : occasions.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="font-sans text-base text-neutral-900 opacity-60 mb-4">
                                No menu planning occasions yet.
                            </p>
                            <button
                                onClick={openScheduleModal}
                                className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                                style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                                onMouseEnter={(e) => e.target.style.opacity = '0.6'}
                                onMouseLeave={(e) => e.target.style.opacity = '1'}
                            >
                                Create Your First Schedule
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {occasions.map((occasion) => {
                                const isGenerating = generatingSchedule && selectedOccasionId === occasion.id;
                                
                                return (
                                    <div
                                        key={occasion.id}
                                        className={`p-4 bg-neutral-50 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all cursor-pointer ${
                                            isGenerating ? 'opacity-60 cursor-wait' : ''
                                        }`}
                                        onClick={() => !isGenerating && handleOccasionClick(occasion.id)}
                                    >
                                        <h3 className="font-sans text-xl font-medium mb-2">
                                            {occasion.name}
                                        </h3>
                                        {occasion.description && (
                                            <p className="font-sans text-sm opacity-80 mb-2">
                                                {occasion.description}
                                            </p>
                                        )}
                                        <p className="font-mono text-xs uppercase tracking-wider opacity-60">
                                            {occasion.recipe_count || 0} recipes
                                        </p>
                                        {isGenerating && (
                                            <p className="font-sans text-sm mt-2 opacity-80">
                                                Generating schedule...
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MenuPlanningOccasionsPage;

